import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  ArrowLeft, Eye, BookOpen, Users, TrendingUp,
  Globe, ArrowUpRight, BarChart3, RefreshCw, Target
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const StatCard = ({ icon: Icon, label, value, sub, color = 'emerald' }) => (
  <Card>
    <CardContent className="pt-5 pb-4">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
          {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
        </div>
        <div className={`w-10 h-10 rounded-xl bg-${color}-100 flex items-center justify-center`}>
          <Icon className={`w-5 h-5 text-${color}-600`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

const BlogAnalytics = () => {
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('views');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchAll();
  }, [sortBy]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [overviewRes, blogsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/blog-analytics/overview`),
        fetch(`${BACKEND_URL}/api/admin/blog-analytics/blogs?sort_by=${sortBy}`)
      ]);
      if (overviewRes.ok) setOverview(await overviewRes.json());
      if (blogsRes.ok) {
        const data = await blogsRes.json();
        setBlogs(data.blogs || []);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const getRateColor = (rate) => {
    if (rate >= 50) return 'text-emerald-600 bg-emerald-50';
    if (rate >= 25) return 'text-amber-600 bg-amber-50';
    return 'text-red-600 bg-red-50';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate('/admin/dashboard')}>
                <ArrowLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              <div>
                <h1 className="text-xl font-bold text-gray-900" data-testid="blog-analytics-title">Blog Performance</h1>
                <p className="text-xs text-gray-500">Track views, read completions & lead conversions</p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={fetchAll} disabled={loading}>
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Overview Stats */}
        {overview && (
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4" data-testid="analytics-overview">
            <StatCard icon={BookOpen} label="Published Blogs" value={overview.total_blogs} color="blue" />
            <StatCard icon={Eye} label="Total Views" value={overview.total_views.toLocaleString()} color="emerald" />
            <StatCard icon={BarChart3} label="Read Completions" value={overview.total_completions.toLocaleString()}
              sub={`${overview.read_completion_rate}% avg read rate`} color="purple" />
            <StatCard icon={Users} label="Blog Leads" value={overview.blog_leads} color="amber" />
            <StatCard icon={Target} label="Blog Appointments" value={overview.blog_appointments} color="rose" />
          </div>
        )}

        {/* Views Trend + Country Performance */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Mini Views Chart */}
          {overview?.views_trend?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-emerald-600" /> Views (Last 7 Days)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-end gap-1 h-32">
                  {overview.views_trend.map((d, i) => {
                    const max = Math.max(...overview.views_trend.map(v => v.views), 1);
                    const height = Math.max(8, (d.views / max) * 100);
                    return (
                      <div key={cat || `cat-${i}`} className="flex-1 flex flex-col items-center gap-1">
                        <span className="text-xs text-gray-500 font-medium">{d.views}</span>
                        <div
                          className="w-full rounded-t-md bg-emerald-500 transition-all"
                          style={{ height: `${height}%` }}
                        />
                        <span className="text-[10px] text-gray-400">
                          {new Date(d.date).toLocaleDateString('en-IN', { weekday: 'short' })}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Country Performance */}
          {overview?.top_countries?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Globe className="w-4 h-4 text-blue-600" /> Country Performance
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {overview.top_countries.map((c, i) => (
                    <div key={ref.source || `ref-${i}`} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <span className="text-lg font-bold text-gray-400 w-6">{i + 1}</span>
                        <div>
                          <span className="font-semibold text-sm text-gray-900">{c.country}</span>
                          <span className="text-xs text-gray-500 ml-2">{c.blogs} blogs</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 text-sm font-medium text-gray-900">
                        <Eye className="w-3.5 h-3.5 text-gray-400" />
                        {c.views.toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Per-Blog Performance Table */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Blog Performance</CardTitle>
              <div className="flex gap-2">
                {['views', 'recent'].map(s => (
                  <Button key={s} size="sm" variant={sortBy === s ? 'default' : 'outline'}
                    onClick={() => setSortBy(s)} className="text-xs h-7"
                    data-testid={`sort-${s}-btn`}>
                    {s === 'views' ? 'Top Views' : 'Most Recent'}
                  </Button>
                ))}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="blog-performance-table">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-2 font-medium text-gray-500 text-xs uppercase">Blog</th>
                    <th className="text-center py-3 px-2 font-medium text-gray-500 text-xs uppercase w-20">Views</th>
                    <th className="text-center py-3 px-2 font-medium text-gray-500 text-xs uppercase w-20">Read %</th>
                    <th className="text-center py-3 px-2 font-medium text-gray-500 text-xs uppercase w-20">Leads</th>
                    <th className="text-center py-3 px-2 font-medium text-gray-500 text-xs uppercase w-24">Conv. %</th>
                    <th className="text-center py-3 px-2 font-medium text-gray-500 text-xs uppercase w-20">Country</th>
                  </tr>
                </thead>
                <tbody>
                  {blogs.map((b, i) => (
                    <tr key={b.slug || `blog-${i}`} className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                      data-testid={`blog-row-${i}`}>
                      <td className="py-3 px-2">
                        <div className="flex items-start gap-2">
                          <div className="min-w-0">
                            <a href={`/blog/${b.slug}`} target="_blank" rel="noopener noreferrer"
                              className="font-medium text-gray-900 hover:text-emerald-600 transition-colors line-clamp-1 flex items-center gap-1">
                              {b.title}
                              <ArrowUpRight className="w-3 h-3 flex-shrink-0 text-gray-400" />
                            </a>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="outline" className="text-[10px] py-0">{(b.category || '').replace('_', ' ')}</Badge>
                              {b.auto_scheduled && <span className="text-[10px] text-purple-500">auto</span>}
                              <span className="text-[10px] text-gray-400">{b.created_at ? new Date(b.created_at).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' }) : ''}</span>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="text-center py-3 px-2 font-semibold text-gray-900">{b.views.toLocaleString()}</td>
                      <td className="text-center py-3 px-2">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${getRateColor(b.read_rate)}`}>
                          {b.read_rate}%
                        </span>
                      </td>
                      <td className="text-center py-3 px-2 font-medium text-gray-700">{b.leads}</td>
                      <td className="text-center py-3 px-2">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${getRateColor(b.conversion_rate * 10)}`}>
                          {b.conversion_rate}%
                        </span>
                      </td>
                      <td className="text-center py-3 px-2">
                        {b.target_country ? (
                          <Badge variant="outline" className="text-[10px] py-0">
                            <Globe className="w-2.5 h-2.5 mr-0.5" />{b.target_country}
                          </Badge>
                        ) : <span className="text-gray-300">-</span>}
                      </td>
                    </tr>
                  ))}
                  {blogs.length === 0 && (
                    <tr>
                      <td colSpan={6} className="text-center py-12 text-gray-400">
                        No published blogs yet. Generate some from the AI Blog Engine!
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BlogAnalytics;
